import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Mail, Eye, EyeOff, Loader2, Info, AlertTriangle, Send,
  Settings2, ChevronDown, ChevronUp
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { getAppSettings, updateAppSettings } from '@/lib/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SECTION_LABELS = {
  conferma_richiesta: {
    riepilogo_costi: { label: 'Riepilogo Costi', desc: 'Tabella con valori articoli e costi gestione' },
    importo_banner: { label: 'Banner Importo', desc: 'Box verde/arancione con rimborso, voucher o pagamento' },
    istruzioni_spedizione: { label: 'Istruzioni Spedizione', desc: 'Box giallo con le istruzioni per il cliente' },
    indirizzo_spedizione: { label: 'Indirizzo Spedizione', desc: 'Indirizzo dove inviare il reso' },
    indirizzo_negozio: { label: 'Indirizzo Negozio', desc: 'Mostra l\'indirizzo del negozio fisico' },
    condizioni_accettate: { label: 'Condizioni Accettate', desc: 'Elenco delle condizioni accettate dal cliente' },
  },
  pacco_in_lavorazione: {
    box_info: { label: 'Box Informativo', desc: 'Box verde con messaggio "la sua richiesta è in lavorazione"' },
  },
  voucher_emesso: {
    codice_voucher: { label: 'Codice Voucher', desc: 'Box verde con codice buono e valore' },
    scadenza_voucher: { label: 'Scadenza Voucher', desc: 'Avviso giallo con data di scadenza' },
  },
  richiesta_rifiutata: {
    motivazione: { label: 'Motivazione Rifiuto', desc: 'Box rosso con la motivazione scritta dall\'admin' },
  },
};

const V2_TEMPLATES = [
  {
    key: 'conferma_richiesta',
    name: 'Conferma Richiesta',
    description: 'Inviata alla creazione di cambio, voucher o reso.',
    color: 'bg-emerald-100 text-emerald-700 border-emerald-300',
    badge: 'TUTTI I TIPI',
    badgeColor: 'bg-emerald-200 text-emerald-800',
    previewKey: 'exchange_confirmation',
  },
  {
    key: 'pacco_in_lavorazione',
    name: 'Pacco in Lavorazione',
    description: 'Inviata quando il pacco arriva in logistica.',
    color: 'bg-indigo-100 text-indigo-700 border-indigo-300',
    badge: 'CAMBI + RESI',
    badgeColor: 'bg-indigo-200 text-indigo-800',
    previewKey: 'exchange_in_progress',
  },
  {
    key: 'voucher_emesso',
    name: 'Voucher Emesso',
    description: 'Inviata quando il voucher viene generato.',
    color: 'bg-green-100 text-green-700 border-green-300',
    badge: 'VOUCHER',
    badgeColor: 'bg-green-200 text-green-800',
    previewKey: 'voucher_approved',
  },
  {
    key: 'richiesta_rifiutata',
    name: 'Richiesta Rifiutata',
    description: 'Inviata quando l\'admin rifiuta la richiesta.',
    color: 'bg-red-100 text-red-700 border-red-300',
    badge: 'TUTTI I TIPI',
    badgeColor: 'bg-red-200 text-red-800',
    previewKey: 'return_rejected',
  },
];

const NON_EMAIL_FLOWS = [
  { action: 'Rimborso approvato (RMA)', handler: 'Shopify', note: 'Email gestita nativamente da Shopify' },
  { action: 'Ritiro in negozio pronto', handler: 'WhatsApp', note: 'Gestito manualmente via WhatsApp' },
  { action: 'Lettera di vettura', handler: 'Template futuro', note: 'Admin notificato — da creare se necessario' },
  { action: 'Approvazione iniziale', handler: 'Template futuro', note: 'Admin notificato — da creare se necessario' },
];

const EMAIL_TOGGLES = [
  { key: 'email_toggle_conferma', label: 'Conferma Richiesta', desc: 'Email alla creazione di cambio/voucher/reso', template: 'conferma_richiesta' },
  { key: 'email_toggle_cambio_lavorazione', label: 'Pacco in Lavorazione', desc: 'Email all\'arrivo del pacco in logistica', template: 'pacco_in_lavorazione' },
  { key: 'email_toggle_voucher_approvato', label: 'Voucher Emesso', desc: 'Invio codice voucher al cliente', template: 'voucher_emesso' },
  { key: 'email_toggle_rifiuto', label: 'Richiesta Rifiutata', desc: 'Notifica di rifiuto con motivazione', template: 'richiesta_rifiutata' },
];

function TemplateCard({ template, token, emailSections, onSectionToggle }) {
  const [showPreview, setShowPreview] = useState(false);
  const [showSections, setShowSections] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [loading, setLoading] = useState(false);

  const sections = SECTION_LABELS[template.key] || {};
  const currentToggles = emailSections?.[template.key] || {};

  const loadPreview = useCallback(async () => {
    if (previewHtml) { setShowPreview(s => !s); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/email-templates/${template.previewKey}/preview`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      if (res.ok) {
        const data = await res.json();
        setPreviewHtml(data.html);
        setShowPreview(true);
      }
    } catch (e) { /* ignore */ }
    setLoading(false);
  }, [template.previewKey, token, previewHtml]);

  return (
    <div className={`bg-white rounded-xl border overflow-hidden ${template.color.split(' ').pop()}`} data-testid={`v2-template-${template.key}`}>
      <div className="px-5 py-4 flex items-center gap-3">
        <div className={`w-9 h-9 rounded-lg ${template.color.split(' ').slice(0, 2).join(' ')} flex items-center justify-center flex-shrink-0`}>
          <Mail className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="font-medium text-gray-900 text-sm">{template.name}</p>
            <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${template.badgeColor}`}>{template.badge}</span>
          </div>
          <p className="text-xs text-gray-500 mt-0.5">{template.description}</p>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {Object.keys(sections).length > 0 && (
            <Button variant="outline" size="sm" onClick={() => setShowSections(s => !s)} className="text-xs" data-testid={`sections-toggle-${template.key}`}>
              <Settings2 className="w-3 h-3 mr-1" />
              Sezioni
              {showSections ? <ChevronUp className="w-3 h-3 ml-1" /> : <ChevronDown className="w-3 h-3 ml-1" />}
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => showPreview ? setShowPreview(false) : loadPreview()} className="text-xs" data-testid={`preview-${template.key}`}>
            {loading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : showPreview ? <EyeOff className="w-3 h-3 mr-1" /> : <Eye className="w-3 h-3 mr-1" />}
            {showPreview ? 'Chiudi' : 'Anteprima'}
          </Button>
        </div>
      </div>

      {/* Section toggles */}
      <AnimatePresence>
        {showSections && Object.keys(sections).length > 0 && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.15 }} className="overflow-hidden">
            <div className="border-t border-gray-100 bg-gray-50/50 px-5 py-3">
              <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-2">Sezioni visibili nell&apos;email</p>
              <div className="space-y-2">
                {Object.entries(sections).map(([secKey, secInfo]) => (
                  <div key={secKey} className="flex items-center justify-between py-1" data-testid={`section-row-${template.key}-${secKey}`}>
                    <div>
                      <p className="text-xs font-medium text-gray-700">{secInfo.label}</p>
                      <p className="text-[10px] text-gray-400">{secInfo.desc}</p>
                    </div>
                    <Switch
                      checked={currentToggles[secKey] !== false}
                      onCheckedChange={(checked) => onSectionToggle(template.key, secKey, checked)}
                      data-testid={`section-toggle-${template.key}-${secKey}`}
                    />
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Preview iframe */}
      <AnimatePresence>
        {showPreview && previewHtml && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2 }} className="overflow-hidden">
            <div className="border-t border-gray-100">
              <iframe srcDoc={previewHtml} title={`Preview ${template.name}`} className="w-full border-0" style={{ height: '600px' }} sandbox="allow-same-origin" data-testid={`preview-iframe-${template.key}`} />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function AdminEmailTemplatesPage() {
  const [appSettings, setAppSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState('');

  useEffect(() => {
    const stored = localStorage.getItem('admin_token') || sessionStorage.getItem('admin_token');
    if (stored) setToken(stored);
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        const s = await getAppSettings();
        setAppSettings(s);
      } catch (e) { /* ignore */ }
      setLoading(false);
    };
    load();
  }, []);

  const handleEmailToggle = async (key, checked) => {
    try {
      await updateAppSettings({ [key]: checked });
      setAppSettings(prev => ({ ...prev, [key]: checked }));
      toast.success(checked ? 'Email attivata' : 'Email disattivata');
    } catch (e) {
      toast.error('Errore nel salvataggio');
    }
  };

  const handleSectionToggle = async (templateKey, sectionKey, checked) => {
    try {
      const currentSections = appSettings.email_sections || {};
      const templateSections = { ...(currentSections[templateKey] || {}), [sectionKey]: checked };
      const newSections = { ...currentSections, [templateKey]: templateSections };
      await updateAppSettings({ email_sections: newSections });
      setAppSettings(prev => ({ ...prev, email_sections: newSections }));
      toast.success(`Sezione ${checked ? 'attivata' : 'disattivata'}`);
    } catch (e) {
      toast.error('Errore nel salvataggio');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>;
  }

  return (
    <div className="max-w-4xl mx-auto px-4 lg:px-0 py-8">
      {/* Header */}
      <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3" data-testid="email-templates-title">
          <div className="w-10 h-10 bg-gray-900 rounded-xl flex items-center justify-center">
            <Mail className="w-5 h-5 text-white" />
          </div>
          Template Email V2
        </h1>
        <p className="text-gray-500 mt-2 text-sm">
          4 template unificati. Usa i toggle per attivare/disattivare l&apos;invio e le sezioni visibili in ogni email.
        </p>
      </motion.div>

      {/* Email Toggles */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mb-6 bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid="email-toggles-section">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-2">
          <Send className="w-4 h-4 text-gray-500" />
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Attivazione Email per Tipo</h3>
            <p className="text-xs text-gray-500 mt-0.5">Disattiva un toggle per impedire l&apos;invio. Il reinvio manuale funziona sempre.</p>
          </div>
        </div>
        <div className="divide-y divide-gray-50">
          {EMAIL_TOGGLES.map(toggle => (
            <div key={toggle.key} className="px-5 py-3 flex items-center justify-between" data-testid={`toggle-row-${toggle.key}`}>
              <div>
                <p className="text-sm font-medium text-gray-800">{toggle.label}</p>
                <p className="text-xs text-gray-400">{toggle.desc}</p>
              </div>
              <Switch checked={appSettings[toggle.key] !== false} onCheckedChange={(checked) => handleEmailToggle(toggle.key, checked)} data-testid={`toggle-${toggle.key}`} />
            </div>
          ))}
        </div>
      </motion.div>

      {/* V2 Template Cards with section toggles */}
      <div className="space-y-3 mb-6" data-testid="v2-templates-list">
        {V2_TEMPLATES.map((tmpl, i) => (
          <motion.div key={tmpl.key} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
            <TemplateCard template={tmpl} token={token} emailSections={appSettings.email_sections || {}} onSectionToggle={handleSectionToggle} />
          </motion.div>
        ))}
      </div>

      {/* Non-email flows */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="mb-6 bg-amber-50 rounded-xl border border-amber-200 overflow-hidden" data-testid="non-email-flows">
        <div className="px-5 py-4 border-b border-amber-100 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-600" />
          <h3 className="text-sm font-semibold text-amber-800">Flussi senza email</h3>
        </div>
        <div className="divide-y divide-amber-100">
          {NON_EMAIL_FLOWS.map(flow => (
            <div key={flow.action} className="px-5 py-3 flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-amber-900">{flow.action}</p>
                <p className="text-xs text-amber-600">{flow.note}</p>
              </div>
              <span className="text-[10px] font-semibold bg-amber-200 text-amber-800 px-2 py-0.5 rounded-full">{flow.handler}</span>
            </div>
          ))}
        </div>
      </motion.div>

      {/* Info */}
      <div className="p-4 bg-gray-50 rounded-xl border border-gray-200 flex items-start gap-3" data-testid="email-templates-info">
        <Info className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
        <div className="text-xs text-gray-500 space-y-1">
          <p>I testi dinamici (istruzioni spedizione, attesa negozio, scadenze) si modificano dal <strong>Pannello di Controllo</strong>.</p>
          <p>Le sezioni disattivate non appariranno nell&apos;email inviata al cliente né nel PDF scaricabile.</p>
        </div>
      </div>
    </div>
  );
}
